import { all, call, getContext, put, select, takeLeading } from 'redux-saga/effects';
import type { Action } from '../utils';
import i18n from '../../../i18n';
import type { IIamCheckOtpParams, IIamCheckPasswordPolicyParams, IIamIdentifyUserParams, IIamResetPasswordParams } from '../../api/domain/iam';
import type { IamApiService } from '../../api/services';
import { IamPasswordValidationError, NotificationType } from '../../enum';
import { addNotification } from '../application/application-actions';
import { logError } from '../logging-api/logging-api-actions';
import {
    cancelPasswordReset,
    cancelPasswordResetFailure,
    cancelPasswordResetSuccess,
    checkOtp,
    checkOtpFailure,
    checkOtpSuccess,
    checkPasswordPolicy,
    checkPasswordPolicyFailure,
    checkPasswordPolicySuccess,
    identifyUser,
    identifyUserFailure,
    identifyUserSuccess,
    resetPassword,
    resetPasswordFailure,
    resetPasswordSuccess,
} from './iam-api-actions';
import { getInvalidPasswordError } from './iam-api-selectors';

function* handleIdentifyUser({ payload: { username } }: Action<IIamIdentifyUserParams>) {
    const api: IamApiService = yield getContext('iamApiService');
    try {
        yield call([api, api.identifyUser], { username });
        yield put(identifyUserSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not identify user on password-reset: ${error}`));
        yield put(identifyUserFailure(error));
    }
}

function* handleIdentifyUserFailure() {
    yield put(
        addNotification({
            title: i18n.t('prospect-pw-reset.identify-user.error.notification.title'),
            type: NotificationType.ERROR,
            text: i18n.t('prospect-pw-reset.identify-user.error.notification.text'),
        }),
    );
}

function* handleCheckOtp({ payload: { otp } }: Action<IIamCheckOtpParams>) {
    const api: IamApiService = yield getContext('iamApiService');
    try {
        yield call([api, api.checkOtp], { otp });
        yield put(checkOtpSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not check OTP on password-reset: ${error}`));
        yield put(checkOtpFailure(error));
    }
}

function* handleCheckOtpFailure() {
    yield put(
        addNotification({
            title: i18n.t('prospect-pw-reset.check-otp.error.notification.title'),
            type: NotificationType.ERROR,
            text: i18n.t('prospect-pw-reset.check-otp.error.notification.text'),
        }),
    );
}

function* handleCheckPasswordPolicy({ payload: { password } }: Action<IIamCheckPasswordPolicyParams>) {
    const api: IamApiService = yield getContext('iamApiService');
    try {
        const result: IamPasswordValidationError = yield call([api, api.checkPasswordPolicy], { password });

        if (Object.values(IamPasswordValidationError).includes(result)) {
            yield put(checkPasswordPolicyFailure(result));
        } else {
            yield put(checkPasswordPolicySuccess());
        }
    } catch (error: unknown) {
        yield put(logError(`given password id invalid password-reset: ${error}`));
        yield put(checkPasswordPolicyFailure(undefined as any));
    }
}

function* handleCheckPasswordPolicyFailure() {
    const passwordPolicyError: IamPasswordValidationError = yield select(getInvalidPasswordError);

    if (!passwordPolicyError) {
        yield put(
            addNotification({
                title: i18n.t('prospect-pw-reset.check-password-policy.error.notification.title'),
                type: NotificationType.ERROR,
                text: i18n.t('prospect-pw-reset.check-password-policy.notification.text'),
            }),
        );
    }
}

function* handleResetPassword({ payload: { newPassword } }: Action<IIamResetPasswordParams>) {
    const api: IamApiService = yield getContext('iamApiService');
    try {
        yield call([api, api.resetPassword], { newPassword });
        yield put(resetPasswordSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not reset password on password-reset: ${error}`));
        yield put(resetPasswordFailure(error));
    }
}

function* handleResetPasswordFailure() {
    yield put(
        addNotification({
            title: i18n.t('prospect-pw-reset.reset-password.error.notification.title'),
            type: NotificationType.ERROR,
            text: i18n.t('prospect-pw-reset.reset-password.notification.text'),
        }),
    );
}

function* handleCancelPasswordReset() {
    const api: IamApiService = yield getContext('iamApiService');
    try {
        yield call([api, api.cancelPasswordReset]);
        yield put(cancelPasswordResetSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not cancel password-reset process: ${error}`));
        yield put(cancelPasswordResetFailure(error));
    }
}

function* handleCancelPasswordResetFailure() {
    yield put(
        addNotification({
            title: i18n.t('prospect-pw-reset.cancel-password-reset.error.notification.title'),
            type: NotificationType.ERROR,
            text: i18n.t('prospect-pw-reset.cancel-password-reset.notification.text'),
        }),
    );
}

export function* iamApiSaga() {
    yield all([
        takeLeading(identifyUser, handleIdentifyUser),
        takeLeading(identifyUserFailure, handleIdentifyUserFailure),
        takeLeading(checkOtp, handleCheckOtp),
        takeLeading(checkOtpFailure, handleCheckOtpFailure),
        takeLeading(checkPasswordPolicy, handleCheckPasswordPolicy),
        takeLeading(checkPasswordPolicyFailure, handleCheckPasswordPolicyFailure),
        takeLeading(resetPassword, handleResetPassword),
        takeLeading(resetPasswordFailure, handleResetPasswordFailure),
        takeLeading(cancelPasswordReset, handleCancelPasswordReset),
        takeLeading(cancelPasswordResetFailure, handleCancelPasswordResetFailure),
    ]);
}
